import { DispatcherEtaShareInfo, EtaShareCodeState } from '../../services/api/domains/EtaShare';
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Logic } from '../../logic/logic';
import { message } from 'antd';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import EtaSharePromo from '../../components/Promo/Promo';
import { useWindowDimensions } from '../../hooks/useWindowDimensions';
import { DISPATCHER_URL, SMALL_DEVICE_WIDTH } from '../../utils/constants/constants';
import { CustomUserEvents, InitiatedFrom } from '../../logic/CustomUserEvents';
import EtaShareInfo from '../../components/EtaShareInfo/EtaShareInfo';
import EtaShareUpdateOn from '../../components/UpdateOn/UpdateOn';
import TryDispatcher from '../../components/TryDispatcher/TryDispatcher';

interface Props {
    logic: Logic;
}

const DispatcherEtaShareModule = (props: Props) => {
    const params = useParams();
    const { t } = useTranslation();
    const { width } = useWindowDimensions();
    const intervalRef = useRef(0);

    const MESSAGE_KEY = 'rl-message-key';
    const REFRESH_INTERVAL = 1 * 60 * 1000; //1min

    const [dispatcherEtaShareInfo, setDispatcherEtaShareInfo] = useState<DispatcherEtaShareInfo | null>();
    const [isShareEnabled, setIsShareEnabled] = useState(false);
    const [isPromoVisible, setIsPromoVisible] = useState(false);

    const messageStyle = {
        width: width > SMALL_DEVICE_WIDTH ? width / 2 : '100%'
    };

    useEffect(() => {
        message.config({
            duration: 0,
            maxCount: 1
        });

        if (params?.code) {
            _getEtaShareInfo(params?.code);

            intervalRef.current = window.setInterval(() => {
                if (params?.code) {
                    _getEtaShareInfo(params?.code);
                }
            }, REFRESH_INTERVAL);
        }

        _showPromo();

        return () => clearInterval(intervalRef.current);
    }, []);

    useEffect(() => {
        const shareEnabled = !!(
            dispatcherEtaShareInfo &&
            dispatcherEtaShareInfo.sharingState === EtaShareCodeState.Active &&
            moment(dispatcherEtaShareInfo.sharingValidUntil) > moment()
        );

        setIsShareEnabled(shareEnabled);

        if (dispatcherEtaShareInfo) {
            switch (dispatcherEtaShareInfo?.sharingState) {
                case EtaShareCodeState.Completed:
                    message.success({
                        content: t('EtaShare.transportCompleted'),
                        key: MESSAGE_KEY,
                        style: messageStyle
                    });
                    _showPromo(true);
                    break;
                case EtaShareCodeState.Cancelled:
                    message.warning({
                        content: `${dispatcherEtaShareInfo?.companyName} ${t('EtaShare.canceledSharing')}`,
                        key: MESSAGE_KEY,
                        style: messageStyle
                    });
                    _showPromo(true);
                    break;
                default:
                    break;
            }
        }
    }, [dispatcherEtaShareInfo]);

    const _onPromoVisibleChange = () => {
        message.destroy(MESSAGE_KEY);
        setIsPromoVisible(!isPromoVisible);
    };

    const _onTryDispatcherButton = () => {
        CustomUserEvents.tryDispatcherButton(InitiatedFrom.TEASER_SHARE_BUTTON, dispatcherEtaShareInfo);
        window.open(DISPATCHER_URL, '_blank');
    };

    const _getEtaShareInfo = async (code: string) => {
        try {
            const result = await props.logic.apiService().etaShare().getDispatcherEtaShareInfo(code);
            setDispatcherEtaShareInfo(result);
        } catch (error: any) {
            console.error(error);

            if (error?.response?.status === 410) {
                _showErrorMessage(t('EtaShare.expiredSharing'));
            }

            if (error?.response?.status === 404) {
                _showErrorMessage(t('EtaShare.doesNotExistSharing'));
            }
        }
    };

    const _showErrorMessage = (text: string) => {
        message.error({
            content: text,
            key: MESSAGE_KEY,
            style: messageStyle
        });

        _showPromo(true);
    };

    const _showPromo = (afterStopSharing = false) => {
        if (width > SMALL_DEVICE_WIDTH || afterStopSharing) {
            setIsPromoVisible(true);
        }

        if (afterStopSharing) {
            clearInterval(intervalRef.current);
        }
    };

    return (
        <>
            <EtaSharePromo
                visible={isPromoVisible}
                isShareEnabled={isShareEnabled}
                onPromoVisibleChange={_onPromoVisibleChange}
                onTryDispatcherButton={_onTryDispatcherButton}
            />
            {isShareEnabled && (
                <>
                    <EtaShareUpdateOn
                        updatedOn={dispatcherEtaShareInfo?.route?.updatedOn}
                        isPromoVisible={isPromoVisible}
                    />
                    <EtaShareInfo
                        isPromoVisible={isPromoVisible}
                        address={dispatcherEtaShareInfo?.route?.destination?.address}
                        routeEta={dispatcherEtaShareInfo?.route?.eta}
                        remainingDistance={dispatcherEtaShareInfo?.route?.remainingDistance}
                        logoUrl={dispatcherEtaShareInfo?.companyLogoUrl}
                        name={dispatcherEtaShareInfo?.companyName}
                    />
                    <div className="dispatcher-eta-share-try">
                        <TryDispatcher onTryDispatcherButton={_onTryDispatcherButton} color={'white'} />
                    </div>
                </>
            )}
        </>
    );
};

export default DispatcherEtaShareModule;
